import React, { useState, useEffect, useCallback } from 'react';
import MainStructure from '../../components/general/MainStructure';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import * as Icon from 'react-feather';

/* --------------------------------------------------------------------------
   Affiliate dashboard.

   Deliberately NOT gated behind a membership: anyone with a free SchedPilot
   account can join the program and start earning, so this page never renders
   returnMembershipNotification().
   -------------------------------------------------------------------------- */

const BASE_URL = 'https://api.schedpilot.com/wp-json/api/v1';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

const money = (n) => '$' + Number(n || 0).toFixed(2);

function StatCard({ label, value, hint, accent }) {
  return (
    <div className="col-6 col-lg-3 mb-3">
      <div className="h-100 p-3 p-lg-4 rounded-4"
           style={{ background: '#fff', border: `1px solid ${accent ? '#bbf7d0' : '#e2e8f0'}` }}>
        <div className="text-uppercase fw-semibold mb-2"
             style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
          {label}
        </div>
        <div className="fw-bold" style={{ fontSize: '1.6rem', color: accent ? '#16a34a' : '#0f172a' }}>
          {value}
        </div>
        {hint && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{hint}</div>}
      </div>
    </div>
  );
}

function Affiliate() {
  const [loading, setLoading]   = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [joining, setJoining]   = useState(false);
  const [partner, setPartner]   = useState(null);
  const [stats, setStats]       = useState(null);
  const [error, setError]       = useState('');
  const [copied, setCopied]     = useState(false);

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year,  setYear]  = useState(today.getFullYear());

  // Payout settings
  const [payoutMethod, setPayoutMethod] = useState('paypal');
  const [payoutEmail,  setPayoutEmail]  = useState('');
  const [savingPayout, setSavingPayout] = useState(false);
  const [payoutSaved,  setPayoutSaved]  = useState(false);

  const loadStats = useCallback(async (m, y) => {
    try {
      const res  = await fetch(`${BASE_URL}/affiliate/stats?month=${m}&year=${y}`, { credentials: 'include' });
      const data = await res.json();

      if (data.code === 200 && data.enrolled) {
        setEnrolled(true);
        setPartner(data.partner);
        setStats(data);
        setPayoutMethod(data.partner.payout_method || 'paypal');
        setPayoutEmail(data.partner.payout_email || '');
      } else if (data.code === 200) {
        setEnrolled(false);
      }
    } catch (err) {
      setError('Could not load your affiliate stats. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(month, year); }, [loadStats, month, year]);

  const handleJoin = async () => {
    setJoining(true);
    setError('');
    try {
      const res  = await fetch(`${BASE_URL}/affiliate/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();

      if (data.code === 200) {
        setEnrolled(true);
        setPartner(data.partner);
        loadStats(month, year);
      } else {
        setError(data.message || 'Could not join the program. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  const handleCopy = () => {
    if (!partner) return;
    navigator.clipboard.writeText(partner.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSavePayout = async (e) => {
    e.preventDefault();
    setSavingPayout(true);
    setPayoutSaved(false);
    try {
      const res = await fetch(`${BASE_URL}/affiliate/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payout_method: payoutMethod, payout_email: payoutEmail }),
      });
      const data = await res.json();

      if (data.code === 200) {
        setPartner(data.partner);
        setPayoutSaved(true);
        setTimeout(() => setPayoutSaved(false), 2500);
      } else {
        setError(data.message || 'Could not save your payout details.');
      }
    } catch (err) {
      setError('Network error while saving payout details.');
    } finally {
      setSavingPayout(false);
    }
  };

  const stepMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1)  { m = 12; y -= 1; }
    if (m > 12) { m = 1;  y += 1; }
    setMonth(m);
    setYear(y);
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <MainStructure activeItem="affiliate">
        <div className="container-fluid p-0"><LoadingSpinner /></div>
      </MainStructure>
    );
  }

  // ── Not enrolled yet: the pitch + a one-click join ────────────────────────
  if (!enrolled) {
    return (
      <MainStructure activeItem="affiliate">
        <div className="container-fluid p-0">
          <h1 className="h3 mb-4">Affiliate Program</h1>

          {error && <div className="alert alert-danger">{error}</div>}

          <div className="mx-auto text-center p-4 p-lg-5 rounded-4"
               style={{ maxWidth: 720, background: '#fff', border: '1px solid #e2e8f0' }}>
            <div className="d-inline-flex align-items-center justify-content-center mb-3"
                 style={{ width: 64, height: 64, borderRadius: 18, background: '#dcfce7' }}>
              <Icon.TrendingUp size={30} color="#16a34a" />
            </div>

            <h2 className="fw-bold mb-2" style={{ fontSize: '1.6rem' }}>
              Earn 30% of every payment
            </h2>
            <p className="mx-auto mb-4" style={{ maxWidth: 480, color: '#64748b' }}>
              Share SchedPilot and earn a recurring 30% commission for as long as the
              people you refer stay subscribed. No membership needed to take part —
              your free account is enough.
            </p>

            <div className="row g-3 mb-4 text-start">
              {[
                { icon: <Icon.Percent size={18} color="#16a34a" />, title: '30% recurring',
                  body: 'Paid on the first payment and every renewal after it.' },
                { icon: <Icon.Clock size={18} color="#16a34a" />, title: '60-day cookie',
                  body: 'They can come back weeks later and you still get credited.' },
                { icon: <Icon.Shield size={18} color="#16a34a" />, title: '30-day hold',
                  body: 'Commissions clear once the refund window closes.' },
              ].map((f) => (
                <div className="col-12 col-md-4" key={f.title}>
                  <div className="h-100 p-3 rounded-3" style={{ background: '#f8fafc' }}>
                    <div className="mb-2">{f.icon}</div>
                    <div className="fw-bold mb-1" style={{ fontSize: '0.95rem' }}>{f.title}</div>
                    <div style={{ fontSize: '0.825rem', color: '#64748b' }}>{f.body}</div>
                  </div>
                </div>
              ))}
            </div>

            <button className="btn btn-lg fw-bold px-4"
                    style={{ background: '#2fba01', color: '#fff', borderRadius: 12 }}
                    onClick={handleJoin} disabled={joining}>
              {joining ? 'Setting up your account…' : 'Join the affiliate program'}
            </button>

            <div className="mt-3" style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              Free to join · no traffic minimums · get your link instantly
            </div>
          </div>
        </div>
      </MainStructure>
    );
  }

  // ── Enrolled: the dashboard ───────────────────────────────────────────────
  const t       = stats?.totals || {};
  const daily   = stats?.daily || [];
  const maxDay  = Math.max(1, ...daily.map((d) => d.clicks));

  return (
    <MainStructure activeItem="affiliate">
      <div className="container-fluid p-0">

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <h1 className="h3 mb-0">Affiliate Program</h1>
          <span className="badge rounded-pill"
                style={{ background: '#dcfce7', color: '#166534', fontSize: '0.75rem', padding: '6px 12px' }}>
            {Math.round((partner?.rate ?? 0.3) * 100)}% recurring commission
          </span>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Referral link */}
        <div className="p-4 rounded-4 mb-4" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
          <div className="fw-bold mb-1">Your referral link</div>
          <p className="mb-3" style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Anyone who opens this link is tracked for {partner?.cookie_days ?? 60} days.
            You earn on every payment they make, for as long as they stay.
          </p>

          <div className="d-flex flex-column flex-sm-row gap-2">
            <input type="text" className="form-control" readOnly value={partner?.url || ''}
                   onFocus={(e) => e.target.select()}
                   style={{ fontFamily: 'monospace', fontSize: '0.875rem', background: '#f8fafc' }} />
            <button className="btn fw-bold px-4"
                    style={{ background: copied ? '#16a34a' : '#0f172a', color: '#fff', whiteSpace: 'nowrap' }}
                    onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>

          <div className="mt-2" style={{ fontSize: '0.775rem', color: '#94a3b8' }}>
            Your code: <code>{partner?.code}</code> — you can append <code>?ref={partner?.code}</code> to
            any page on schedpilot.com.
          </div>
        </div>

        {/* Totals */}
        <div className="row">
          <StatCard label="Clicks"    value={(t.clicks ?? 0).toLocaleString()} />
          <StatCard label="Signups"   value={t.signups ?? 0} hint={`${t.conversion_rate ?? 0}% of clicks`} />
          <StatCard label="Customers" value={t.customers ?? 0} hint="Referrals who have paid" />
          <StatCard label="Lifetime earnings" value={money(t.lifetime_earnings)} accent />
        </div>

        <div className="row">
          <StatCard label="Pending"
                    value={money(t.pending_earnings)}
                    hint={`In the ${partner?.hold_days ?? 30}-day refund window`} />
          <StatCard label="Ready for payout"
                    value={money(t.available_earnings)}
                    hint="Cleared — paid on the next run" accent />
          <StatCard label="Already paid" value={money(t.paid_earnings)} />
        </div>

        {/* Clicks by month */}
        <div className="rounded-4 mb-4" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
          <div className="d-flex justify-content-between align-items-center p-3 p-lg-4"
               style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div className="fw-bold">Clicks — {MONTHS[month - 1]} {year}</div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => stepMonth(-1)}>
                <Icon.ChevronLeft size={14} />
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => stepMonth(1)}>
                <Icon.ChevronRight size={14} />
              </button>
            </div>
          </div>

          <div className="p-3 p-lg-4">
            {daily.every((d) => d.clicks === 0) ? (
              <div className="text-center py-4" style={{ color: '#94a3b8' }}>
                No clicks recorded this month yet.
              </div>
            ) : (
              <div className="d-flex align-items-end gap-1" style={{ height: 160 }}>
                {daily.map((d) => (
                  <div key={d.date} className="flex-fill d-flex flex-column justify-content-end align-items-center"
                       title={`${d.date}: ${d.clicks} click${d.clicks === 1 ? '' : 's'}`}>
                    <div style={{
                      width: '100%',
                      height: `${(d.clicks / maxDay) * 130}px`,
                      minHeight: d.clicks > 0 ? 3 : 0,
                      background: '#16a34a',
                      borderRadius: '3px 3px 0 0',
                    }} />
                    <div style={{ fontSize: '0.6rem', color: '#94a3b8', marginTop: 4 }}>
                      {d.day % 5 === 0 || d.day === 1 ? d.day : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="row">
          {/* Referrals */}
          <div className="col-12 col-xl-6 mb-4">
            <div className="rounded-4 h-100" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
              <div className="p-3 p-lg-4 fw-bold" style={{ borderBottom: '1px solid #f1f5f9' }}>
                Recent signups
              </div>
              {stats?.referrals?.length ? (
                <div className="table-responsive">
                  <table className="table mb-0" style={{ fontSize: '0.875rem' }}>
                    <thead><tr><th>Email</th><th>Status</th><th>Date</th></tr></thead>
                    <tbody>
                      {stats.referrals.map((r, i) => (
                        <tr key={i}>
                          <td>{r.referred_email}</td>
                          <td>
                            <span className="badge rounded-pill" style={{
                              background: r.status === 'converted' ? '#dcfce7' : '#f1f5f9',
                              color:      r.status === 'converted' ? '#166534' : '#475569',
                            }}>
                              {r.status === 'converted' ? 'Customer' : 'Signed up'}
                            </span>
                          </td>
                          <td style={{ color: '#64748b' }}>{(r.created_at || '').split(' ')[0]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5" style={{ color: '#94a3b8' }}>
                  No signups yet. Share your link to get started.
                </div>
              )}
            </div>
          </div>

          {/* Commissions */}
          <div className="col-12 col-xl-6 mb-4">
            <div className="rounded-4 h-100" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
              <div className="p-3 p-lg-4 fw-bold" style={{ borderBottom: '1px solid #f1f5f9' }}>
                Commissions
              </div>
              {stats?.commissions?.length ? (
                <div className="table-responsive">
                  <table className="table mb-0" style={{ fontSize: '0.875rem' }}>
                    <thead><tr><th>Email</th><th>Sale</th><th>You earn</th><th>Status</th></tr></thead>
                    <tbody>
                      {stats.commissions.map((c, i) => (
                        <tr key={i}>
                          <td>{c.referred_email}</td>
                          <td style={{ color: '#64748b' }}>{money(c.payment_amount)}</td>
                          <td className="fw-bold">{money(c.amount)}</td>
                          <td>
                            <span className="badge rounded-pill" style={{
                              background: c.status === 'paid' ? '#dcfce7'
                                        : c.status === 'refunded' ? '#fee2e2' : '#fef9c3',
                              color:      c.status === 'paid' ? '#166534'
                                        : c.status === 'refunded' ? '#991b1b' : '#854d0e',
                            }}>
                              {c.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5" style={{ color: '#94a3b8' }}>
                  No commissions yet.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payout details */}
        <div className="p-4 rounded-4 mb-4" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
          <div className="fw-bold mb-1">Payout details</div>
          <p className="mb-3" style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Where should we send your commissions? Payouts run monthly, once the
            {' '}{partner?.hold_days ?? 30}-day refund window has closed.
          </p>

          <form onSubmit={handleSavePayout}>
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Method</label>
                <select className="form-select" value={payoutMethod}
                        onChange={(e) => setPayoutMethod(e.target.value)}>
                  <option value="paypal">PayPal</option>
                  <option value="wise">Wise</option>
                </select>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Payout email</label>
                <input type="email" className="form-control" value={payoutEmail}
                       onChange={(e) => setPayoutEmail(e.target.value)}
                       placeholder="you@example.com" />
              </div>
              <div className="col-12 col-md-3">
                <button type="submit" className="btn w-100 fw-bold"
                        style={{ background: payoutSaved ? '#16a34a' : '#0f172a', color: '#fff' }}
                        disabled={savingPayout}>
                  {savingPayout ? 'Saving…' : payoutSaved ? 'Saved!' : 'Save details'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Terms */}
        <div className="p-4 rounded-4 mb-4" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>How it works</div>
          <ul className="mb-0" style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.8 }}>
            <li>You earn {Math.round((partner?.rate ?? 0.3) * 100)}% of every payment your referrals
                make — the first one and every renewal after it.</li>
            <li>Clicks are tracked for {partner?.cookie_days ?? 60} days, using last-click attribution.</li>
            <li>Commissions stay pending for {partner?.hold_days ?? 30} days to cover the refund
                window, then move to “ready for payout”.</li>
            <li>Self-referrals don’t earn commission.</li>
            <li>Please don’t bid on SchedPilot branded keywords or post the link to coupon sites.</li>
          </ul>
        </div>

      </div>
    </MainStructure>
  );
}

export default Affiliate;
